import { Location } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PartnerIncomeTaxStateService } from './partner-income-tax-state.service';

/** Seção interna aberta (abas sublinhadas abaixo dos pontos de atenção), no `?section=` da URL. */
export const PARTNER_INCOME_TAX_SECTION_PARAM = 'section';

/** Abas internas do sócio. */
export type PartnerSectionKey = 'assets' | 'debts' | 'mutation' | 'income-test' | 'documents';
/** Abas internas do consolidado. */
export type ConsolidatedSectionKey = 'comparison' | 'coverage' | 'documents';
export type PartnerIncomeTaxSectionKey = PartnerSectionKey | ConsolidatedSectionKey;

const ALL_SECTIONS: readonly PartnerIncomeTaxSectionKey[] = [
  'assets',
  'debts',
  'mutation',
  'income-test',
  'documents',
  'comparison',
  'coverage',
];

/**
 * Seção aberta na visão do sócio ou do consolidado (redesenho do protótipo 01, card 3515). Cada visão resolve a seção
 * pedida contra as que ela tem (`resolve`): pedida "assets" no consolidado cai no "comparison", e vice-versa. Grava o
 * `?section=` como o `?partner=` (só fora do modo embutido). Provido pelo componente da aba.
 */
@Injectable()
export class PartnerIncomeTaxSectionState {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly state = inject(PartnerIncomeTaxStateService);

  /** Seção pedida (URL ou clique); `null` = a padrão de cada visão. */
  readonly requested = signal<PartnerIncomeTaxSectionKey | null>(this.readFromUrl());

  /** A seção pedida, se a visão a tem; senão a primeira da visão. */
  resolve<T extends PartnerIncomeTaxSectionKey>(available: readonly T[]): T {
    const requested = this.requested();
    return available.includes(requested as T) ? (requested as T) : available[0];
  }

  select(key: PartnerIncomeTaxSectionKey): void {
    this.requested.set(key);
    if (!this.state.syncsUrl) return;
    // Só reescreve o endereço, sem navegar: com `scrollPositionRestoration: 'enabled'` toda navegação rola a página
    // para o topo, e trocar de aba interna jogava a pessoa lá para cima (Victor, 28/10/2026).
    const tree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: { [PARTNER_INCOME_TAX_SECTION_PARAM]: key },
      queryParamsHandling: 'merge',
    });
    this.location.replaceState(this.router.serializeUrl(tree));
  }

  private readFromUrl(): PartnerIncomeTaxSectionKey | null {
    if (!this.state.syncsUrl) return null;
    const value = this.route.snapshot?.queryParamMap?.get(PARTNER_INCOME_TAX_SECTION_PARAM);
    return ALL_SECTIONS.includes(value as PartnerIncomeTaxSectionKey)
      ? (value as PartnerIncomeTaxSectionKey)
      : null;
  }
}
